import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  getEventDetails, 
  getEventAssignments, 
  updateEventDescription,
  getEventContacts,
  updateEventContact,
  getEventEquipment,
  getEventMedia,
  createEventIssue,
  getEventIssues,
  updateEventStatus,
  removeUserFromEvent,
  addEventContacts,
  deleteEventContact,
  getEventRating,
  submitEventRating,
  updateEventEquipment,
  deleteEventEquipment,
  addEquipmentToEvent,
  updateGoogleEventEquipment,
  updateGoogleEventNotes,
  updateGoogleEventContacts,
  makeGoogleApiRequest
} from '../../services/supabase';
import EventAssignUserForm from '../../components/Events/EventAssignUserForm';
import { getInitials } from '../../utils/utils';
import MediaUploader from '../../components/MediaUploader/MediaUploader';
import { supabase } from '../../services/supabase';
import { SquarePen } from 'lucide-react';
import EventStatusBadge from '../../components/Events/EventStatusBadge';
import DeleteEventButton from '../../components/ManagerPermission/DeleteEventButton'
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import EventLocationLink from '../../components/ManagerPermission/EventLocationLink';
import DeleteTeamMemberButton from '../../components/ManagerPermission/DeleteTeamMemberButton';
import AddTeamMemberButton from '../../components/ManagerPermission/AddTeamMemberButton';
import { ManagerOnlyContactActions, ManagerOnlyEditableContactActions } from '../../components/ManagerPermission/ContactActions';
import { useRoleContext } from '../../context/RoleContext';
import AddEventContact from '../../components/ManagerPermission/AddEventContact';
import EditEventDateTimeButton from '../../components/ManagerPermission/EditEventDateTimeButton';
import ManageEventEquipmentButton from '../../components/ManagerPermission/ManageEventEquipmentButton';
import { DefaultButton, PrimaryButton } from '../../components/Common/BaseButton';
import VenueTypeIndicator from '../../components/Events/EventVenueIndicator';
import SelectionModal from '../../components/Common/SelectionModal';
import { updateEventVenueType } from '../../services/supabase';
import EventTypeBadge from '../../components/Events/EventTypeBadge';
import RevenueBadge from '../../components/Events/EventRevenueBadge';

const PageContainer = styled.div`
  padding: 1rem;
  // max-width: 1200px;
  margin: 0 5rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    margin: 0 auto;
  }
`;

const PageTitle = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin: 0;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    gap: 0;
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  width: 100%;
  box-sizing: border-box;
  overflow-wrap: break-word; /* Allow long words to break */
  word-wrap: break-word; /* Fallback for older browsers */
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
    overflow-x: auto; /* Allow horizontal scrolling if needed on mobile */
  }
  
  h3 {
    margin-top: 0;
    color: #333;
    margin-bottom: 1rem;
  }
`;

const PersonList = styled.div`
  margin-top: 1rem;
`;

const Person = styled.div`
  display: flex;
  align-items: center;
  padding: 0.8rem 0;
  border-bottom: 1px solid #eee;
  position: relative;
  min-width: 0; /* Allow flex children to shrink */
  
  &:last-child {
    border-bottom: none;
  }
`;

const PersonAvatar = styled.div`
  width: 32px;
  height: 32px;
  background-color: #f0f0f0;
  border-radius: 50%;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #666;
`;

const PersonInfo = styled.div`
  flex: 1;
  min-width: 0; /* Allow text to truncate */
  overflow: hidden; /* Hide overflow */
`;

const PersonName = styled.div`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    white-space: normal; /* Allow wrapping on mobile */
  }
`;

const PersonRole = styled.div`
  font-size: 0.8rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    white-space: normal; /* Allow wrapping on mobile */
  }
`;

const EventEquipmentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const EventEquipmentItem = styled.li`
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  overflow-wrap: anywhere;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DescriptionContainer = styled.div`
  position: relative;
`;

const DescriptionHeading = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  h3 {
    margin: 0;
    margin-right: 0.5rem;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  display: inline-flex;
  align-items: center;
  margin-left: 0.25rem;
  
  &:hover {
    color: var(--primary-green);
  }
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    border-color: var(--primary-green);
    outline: none;
  }
`;

const SaveCancelButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: 0.5rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 0.5rem;
  }
`;

const formatDate = (dateString) => {
  
  if (!dateString) return 'Invalid date';

  try {
    let date;
    
    // Check if it's a simple date format (YYYY-MM-DD)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day, 12, 0, 0);
    } else {
      // Handle ISO format by removing the timezone offset
      const dateWithoutTimezone = dateString.replace(/[+-]\d{2}:\d{2}$/, '');
      date = new Date(dateWithoutTimezone);
    }

    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

const PageHeaderFlex = styled.div`
  display: grid;
  // grid-template-columns: 2fr 1fr;
  align-items: flex-start;
  // margin-bottom: 1.5rem;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const EventInfoSection = styled.div`
  flex: 1;
  // padding-right: 2rem;
  @media (max-width: 768px) {
    padding-right: 0;
    width: 100%;
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Contact = styled.div`
  display: flex;
  align-items: ${props => props.$isEditing ? 'flex-start' : 'center'};
  flex-direction: ${props => props.$isEditing ? 'column' : 'row'};
  gap: 0.5rem;
  font-size: 0.9rem;
  width: 100%;
`;

const ContactIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #495057;
  font-weight: bold;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactName = styled.div`
  font-weight: 500;
  color: #343a40;
`;

const ContactDetail = styled.div`
  color: #6c757d;
  font-size: 0.8rem;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 2px solid #eee;
  margin-bottom: 1rem;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.$active ? 'var(--primary-green)' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary-green)' : '#333'};
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  padding: 1rem 1.5rem;
  cursor: pointer;
  font-size: 1rem;
  width: 100%;
  white-space: nowrap;
  outline: none;
  transition: transform 0.2s;
  will-change: transform;

  &:hover {
    color: var(--primary-green-dark);
  }
`;

const IssueForm = styled.form`
  margin-top: 1rem;
`;

const IssueOptions = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 1rem;
`;

const IssueLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.5rem;
  cursor: pointer;

  input[type="checkbox"] {
    margin-right: 0.7em;
    accent-color: var(--primary-green);
    width: 18px;
    height: 18px;
  }
`;

const IssueTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 0.7rem;

  &:focus {
    border-color: var(--primary-green);
    outline: none;
  }
`;

const IssueSubmitButton = styled.button`
  background-color: var(--primary-green);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.7rem 1.2rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: var(--primary-green-dark);
  }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const IssuesDivider = styled.div`
  margin: 24px 0;
  border-top: 1px solid #e0e0e0;
`;

const IssuesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const IssuesTitle = styled.h4`
  margin: 0;
  color: #333;
  font-size: 1.1rem;
`;

const IssuesCount = styled.span`
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.9rem;
  color: #666;
`;

const IssuesList = styled.div`
  display: grid;
  gap: 12px;
`;

const IssueItem = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  transition: transform 0.2s ease;
  will-change: transform;

  &:hover {
    background: #f0f0f0;
  }
`;

const IssueType = styled.div`
  display: inline-block;
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-right: 8px;
  margin-bottom: 8px;
`;

const IssueDescription = styled.p`
  margin: 8px 0;
  color: #333;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const IssueMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.85rem;
  color: #666;
`;

const Timestamp = styled.span`
  color: #888;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  // margin-bottom: 1rem;
  color: #666;
  font-size: 0.9rem;
`;

const BreadcrumbLink = styled(Link)`
  color: var(--primary-green);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #999;
`;

const TeamMemberHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const CountBadge = styled.div`
    background: #f0f0f0;
    padding: 4px 8px;
    border-radius: 50%;
    font-size: 0.8rem;
    color: #666;
    margin-left: 0.5rem;
`;

const QuantityBadge = styled.span`
  display: inline-block;
  background: #e0e0e0;
  color: #555;
  font-size: 0.85em;
  font-weight: 500;
  border-radius: 10px;
  padding: 2px 8px;
  margin-left: 8px;
  vertical-align: middle;
`;

const EventCompany = styled.div`
  color: #666;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Helper to update Google Calendar attendees
const updateGoogleEventAttendees = async (googleEventId, attendees) => {
  const accessToken = localStorage.getItem('googleAccessToken');
  if (!accessToken) {
    alert('No Google access token found. Please re-authenticate.');
    return;
  }

  const response = await makeGoogleApiRequest(`https://www.googleapis.com/calendar/v3/calendars/primary/events/${googleEventId}`, {
    method: 'PATCH',
    body: JSON.stringify({ attendees }),
    params: { sendUpdates: 'all' }
  });

  if (!response.ok) {
    throw new Error('Failed to update Google Calendar event attendees');
  }
};

const ContactInput = styled.input`
  width: 100%;
  padding: 0.4rem 0.7rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 0.3rem;
  font-size: 1rem;
`;

const ContactDisplay = memo(({ contact, onEdit, onDelete, isManager, isAuthenticated }) => (
  <Contact $isEditing={false}>
    <ContactIcon>{getInitials(contact.name)}</ContactIcon>
    <ContactInfo>
      <ContactName>{contact.name}</ContactName>
      {contact.role && <ContactDetail>{contact.role}</ContactDetail>}
      {contact.phone && (
        <ContactDetail>
          <a href={`tel:${contact.phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {contact.phone}
          </a>
        </ContactDetail>
      )}
      {contact.email && (
        <ContactDetail>
          <a href={`mailto:${contact.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {contact.email}
          </a>
        </ContactDetail>
      )}
    </ContactInfo>
    <ManagerOnlyContactActions 
      contact={contact}
      onEdit={onEdit}
      onDelete={onDelete}
      isManager={isManager}
      isAuthenticated={isAuthenticated}
    />
  </Contact>
));

const ContactEditForm = memo(({ contact, editingContact, setEditingContact, onCancel, onSave, isManager, isAuthenticated }) => {
  const handleChange = useCallback((field) => (e) => {
    setEditingContact(prev => ({ ...prev, [field]: e.target.value }));
  }, [setEditingContact]);

  return (
    <Contact $isEditing={true}>
      <ContactIcon>{getInitials(contact.name)}</ContactIcon>
      <ContactInput
        type="text"
        value={editingContact.name}
        onChange={handleChange('name')}
        placeholder="Name"
      />
      <ContactInput
        type="text"
        value={editingContact.role}
        onChange={handleChange('role')}
        placeholder="Role"
      />
      <ContactInput
        type="tel"
        value={editingContact.phone}
        onChange={handleChange('phone')}
        placeholder="Phone"
      />
      <ContactInput
        type="email"
        value={editingContact.email}
        onChange={handleChange('email')}
        placeholder="Email"
      />
      <ManagerOnlyEditableContactActions 
        onCancel={onCancel}
        onSave={onSave}
        isManager={isManager}
        isAuthenticated={isAuthenticated}
      />
    </Contact>
  );
});

const EventDetailsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isManager, isAuthenticated } = useRoleContext();
  const [event, setEvent] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [editedLocation, setEditedLocation] = useState('');
  const [editedLocationValue, setEditedLocationValue] = useState(null);
  const [editedLocationCoords, setEditedLocationCoords] = useState({
    lat: null,
    lng: null,
    placeId: null
  });
  const [savingLocation, setSavingLocation] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [status, setStatus] = useState('');
  const [activeTab, setActiveTab] = useState("Pre-Event");
  const tabList = ["Pre-Event", "During Event", "Post-Event"];
  const [uploads, setUploads] = useState([]);
  const [issues, setIssues] = useState([]);
  const [submittingIssue, setSubmittingIssue] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', role: '', phone: '', email: '' });
  const [editingContactId, setEditingContactId] = useState(null);
  const [editingContact, setEditingContact] = useState({ name: '', role: '', phone: '', email: '' });
  const [selectedRating, setSelectedRating] = useState(null);
  const [isRatingSubmitted, setIsRatingSubmitted] = useState(false);
  const [ratingError, setRatingError] = useState(null);
  const [isVenueTypeModalOpen, setIsVenueTypeModalOpen] = useState(false);
  const [isUpdatingVenueType, setIsUpdatingVenueType] = useState(false);

  const managerPermissionProps = useMemo(() => ({
    isManager,
    isAuthenticated
  }), [isManager, isAuthenticated]);


  // Fetch event details and related data
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch event details
        const { data: eventData, error: eventError } = await getEventDetails(eventId);
        if (eventError) throw eventError;
        setEvent(eventData);
        const status = getEventStatus(eventData);
        setStatus(status);
        // Fetch assignments
        const { data: assignmentsData, error: assignmentsError } = await getEventAssignments(eventId);
        if (assignmentsError) throw assignmentsError;
        setAssignments(assignmentsData || []);
        
        // Fetch equipment
        const { data: equipmentData, error: equipmentError } = await getEventEquipment(eventId);
        if (equipmentError) throw equipmentError;
        setEquipment(equipmentData || []);

        // Fetch contacts
        const { data: contactsData, error: contactsError } = await getEventContacts(eventId);
        if (contactsError) throw contactsError;
        setContacts(contactsData || []);

        //fetch media
        const { data: mediaData, error: mediaError } = await getEventMedia(eventId);
        if (mediaError) throw mediaError;
        setUploads(mediaData || []);

      } catch (err) {
        setError(err.message || 'Failed to load event data.');
        console.error(err);
    } finally {
        setLoading(false);
      }
    };
    
    if (eventId) {
      fetchEventData();
    }
  }, [eventId]);

  useEffect(() => {
    const fetchIssues = async () => {
      if (eventId) {
        const { data } = await getEventIssues(eventId);
        if (data) {
          setIssues(data);
        }
      }
    };
    fetchIssues();
  }, [eventId]);

  useEffect(() => {
    const fetchRating = async () => {
      const { data } = await getEventRating(eventId);
      if (data) {
        setSelectedRating(data.rating);
        setIsRatingSubmitted(true);
      }
    };
    fetchRating();
  }, [eventId]);

  useEffect(() => {
    // Update document title when event is loaded
    if (event && event.title) {
      document.title = `${event.title} - WeGo Golf`;
    }
    
    return () => {
      document.title = 'WeGo Golf Employee Portal';
    };
  }, [event]);

  const getEventStatus = (event) => {
    const now = new Date();
    const start = new Date(`${event.start_time}`);
    const end = new Date(`${event.end_time}`);
  
    if (now < start) return "Pre-Event";
    if (now >= start && now <= end) return "During Event";
    if (now > end) return "Post-Event";
    return "unknown";
  }
  
  const handleEditDescription = () => {
    setEditedDescription(event.description || '');
    setIsEditingDescription(true);
  };

  const handleCancelEdit = () => {
    setIsEditingDescription(false);
  };

  const handleSaveDescription = async () => {
    try {
      setSavingDescription(true);
      
      const { error } = await updateEventDescription(eventId, editedDescription);
      
      if (error) throw error;
      
      // Update the local event state with the new description
      setEvent({ ...event, description: editedDescription });
      updateGoogleEventNotes(event.google_event_id, editedDescription);
      setIsEditingDescription(false);
    } catch (err) {
      console.error('Failed to update description:', err);
      alert('Failed to update description. Please try again.');
    } finally {
      setSavingDescription(false);
    }
  };

  // Add these functions for location editing
  const handleEditLocation = () => {
    setEditedLocation(event.location || '');
    setEditedLocationCoords({
      lat: event.location_lat || null,
      lng: event.location_lng || null,
      placeId: event.location_place_id || null
    });
    setIsEditingLocation(true);
  };

  const handleLocationSelect = (place) => {
    setEditedLocationValue(place);
    
    if (place && place.value) {
      setEditedLocation(place.label);
      
      // Create a PlacesService instance with a map or div element
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      
      placesService.getDetails(
        {
          placeId: place.value.place_id,
          fields: ['geometry', 'formatted_address']
        },
        (placeResult, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setEditedLocationCoords({
              lat: placeResult.geometry.location.lat(),
              lng: placeResult.geometry.location.lng(),
              placeId: place.value.place_id
            });
            
            setEditedLocation(placeResult.formatted_address);
          }
        }
      );
    }
  };

  const handleCancelLocationEdit = () => {
    setIsEditingLocation(false);
  };

  const handleLocationUpdate = (updatedEvent) => {
    setEvent(updatedEvent);
  };
  
  const handleUpload = (fileData, formData) => {
    // Add the new upload to the state
    setUploads([...uploads, {
      id: uploads.length + 1,
      event_name: formData.eventName,
      event_date: formData.eventDate,
      file_type: fileData.media_url.split('.').pop(),
      created_at: new Date().toISOString(),
      url: supabase.storage.from('media').getPublicUrl(fileData.path).publicURL
    }]);
  };

  const handleIssueSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      alert('Please log in to submit issues');
      return;
    }

    setSubmittingIssue(true);
    try {
      const formData = new FormData(e.target);
      const selectedIssues = formData.getAll('issue');
      const description = formData.get('description');

      if (selectedIssues.length === 0) {
        alert('Please select at least one issue type');
        return;
      }

      // Create a single issue with all selected types combined
      const { error } = await createEventIssue({
        eventId,
        issueType: selectedIssues.join(', '), // Combine multiple issues with comma
        description,
        userId: user.id
      });

      if (error) throw error;
      
      // Refresh the issues list
      const { data: updatedIssues } = await getEventIssues(eventId);
      if (updatedIssues) {
        setIssues(updatedIssues);
      }

      // Reset the form
      e.target.reset();
    } catch (error) {
      console.error('Error submitting issue:', error);
      alert('Failed to submit issue. Please try again.');
    } finally {
      setSubmittingIssue(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setIsUpdatingStatus(true);
      const { error } = await updateEventStatus(eventId, newStatus);
      
      if (error) throw error;
      
      // Update the local event state
      setEvent({ ...event, event_status: newStatus });
      setIsStatusModalOpen(false);
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update event status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleRemoveEmployee = async (assignmentId) => {
    const confirmed = window.confirm('Are you sure you want to remove this team member from the event?');
    if (!confirmed) return;

    try {
      const assignment = assignments.find(a => a.id === assignmentId);
      const userEmail = assignment?.user?.email;
      if (!userEmail) throw new Error('User email not found');

      if (event.google_event_id) {
        const remainingAssignments = assignments.filter(a => a.id !== assignmentId);
        const attendees = remainingAssignments
          .map(a => a.user?.email)
          .filter(Boolean)
          .map(email => ({ email }));

        await updateGoogleEventAttendees(event.google_event_id, attendees);
      }

      const { data, error } = await removeUserFromEvent(assignmentId);
      if (error) throw error;
      setAssignments(prevAssignments => prevAssignments.filter(a => a.id !== assignmentId));
    } catch (err) {
      console.error('Failed to remove team member:', err);
      alert('Failed to remove team member. Please try again.');
    }
  };

  const handleTeamMembersAdded = async (newAssignments) => {
    // Convert single assignment to array if needed
    const assignmentsArray = Array.isArray(newAssignments) ? newAssignments : [newAssignments];
    
    const userIds = assignmentsArray.map(assignment => assignment.user_id); 
    
    const {data, error} = await supabase.rpc('get_users_by_ids_with_name', {
      user_ids: userIds
    });
    
    
    // Format the data to match the expected structure
    const formattedAssignments = data.map(user => ({
      id: assignmentsArray[0].id, // Use the assignment ID from the new assignment
      user: {
        id: user.id,
        name: user.name,
        email: user.email
      }
    }));

    // 1. Get all current assignments (including new ones)
    const allAssignments = [...assignments, ...formattedAssignments];
    const attendees = allAssignments
      .map(a => a.user?.email)
      .filter(Boolean)
      .map(email => ({ email }));
    // 2. Update Google Calendar event
    if (event.google_event_id) {
      try {
        await updateGoogleEventAttendees(event.google_event_id, attendees);
      } catch (err) {
        alert('Failed to update Google Calendar event attendees.');
        console.error(err);
      }
    }

    setAssignments(allAssignments);
    setIsAddingTeamMember(false);
  };

  // Add a new contact
  const handleAddContact = useCallback(async () => {
    if (!newContact.name.trim()) {
      alert('Contact name is required');
      return;
    }
    try {
      const contactToSave = { ...newContact, event_id: eventId };
      const { data, error } = await addEventContacts([contactToSave]);
      if (error) throw error;
      
      // Calculate the updated contacts array
      const updatedContacts = [...contacts, ...data];
      
      // Update local state
      setContacts(updatedContacts);
      setNewContact({ name: '', role: '', phone: '', email: '' });
      setIsAddingContact(false);
      
      // Fire off Google Calendar update in background
      if (event?.google_event_id) {
        updateGoogleEventContacts(event.google_event_id, updatedContacts).catch(error => {
          console.error('Failed to update Google Calendar:', error);
        });
      }
    } catch (err) {
      alert('Failed to add contact.');
      console.error(err);
    }
  }, [newContact, eventId, contacts, event?.google_event_id]);

  const handleEditContact = useCallback((contact) => {
    setEditingContactId(contact.id);
    setEditingContact({ ...contact });
  }, []);

  const handleDeleteContact = useCallback(async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      const { error } = await deleteEventContact(contactId);
      if (error) throw error;
      
      // Calculate the updated contacts array
      const updatedContacts = contacts.filter(c => c.id !== contactId);
      
      // Update local state
      setContacts(updatedContacts);
      
      // Fire off Google Calendar update in background
      if (event?.google_event_id) {
        updateGoogleEventContacts(event.google_event_id, updatedContacts).catch(error => {
          console.error('Failed to update Google Calendar:', error);
        });
      }
    } catch (err) {
      alert('Failed to delete contact.');
      console.error(err);
    }
  }, [contacts, event?.google_event_id]);

  const handleRatingSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRating) {
      alert('Please select a rating');
      return;
    }
    const {error} = await submitEventRating(eventId, selectedRating, user.id);
    if (error){
      setRatingError(error.message);
      return;
    }
    setIsRatingSubmitted(true);
    setRatingError(null);
  };

  const handleSaveEditContact = useCallback(async () => {
    try {
      const { data, error } = await updateEventContact(editingContactId, editingContact);
      if (error) throw error;
      
      const updatedContacts = contacts.map(c => c.id === editingContactId ? data : c);
      
      setContacts(updatedContacts);
      setEditingContactId(null);
      setEditingContact({ name: '', role: '', phone: '', email: '' });
      
      if (event?.google_event_id) {
        updateGoogleEventContacts(event.google_event_id, updatedContacts).catch(error => {
          console.error('Failed to update Google Calendar:', error);
        });
      }
      
    } catch (err) {
      alert('Failed to update contact.');
      console.error(err);
    }
  }, [editingContactId, editingContact, contacts, event?.google_event_id]);

  const handleCancelEditContact = useCallback(() => {
    setEditingContactId(null);
    setEditingContact({ name: '', role: '', phone: '', email: '' });
  }, []);

  // Move memoizedContactsList inside the component
  const memoizedContactsList = useMemo(() => (
    <ContactList>
      {contacts.length === 0 ? (
        <p style={{ color: '#888', fontStyle: 'italic' }}>No contacts for this event.</p>
      ) : (
        contacts.map(contact => (
          <React.Fragment key={contact.id}>
            {editingContactId === contact.id ? (
              <ContactEditForm
                contact={contact}
                editingContact={editingContact}
                setEditingContact={setEditingContact}
                onCancel={handleCancelEditContact}
                onSave={handleSaveEditContact}
                isManager={isManager}
                isAuthenticated={isAuthenticated}
              />
            ) : (
              <ContactDisplay
                contact={contact}
                onEdit={handleEditContact}
                onDelete={handleDeleteContact}
                isManager={isManager}
                isAuthenticated={isAuthenticated}
              />
            )}
          </React.Fragment>
        ))
      )}
    </ContactList>
  ), [contacts, editingContactId, editingContact, handleEditContact, handleDeleteContact, handleCancelEditContact, handleSaveEditContact, isManager, isAuthenticated]);

  const handleDateTimeUpdate = (updatedEvent) => {
    // Update your event state with the new values
    setEvent(updatedEvent);
  };

  const handleEquipmentChange = async (newEquipmentList) => {
    const { data: current } = await getEventEquipment(eventId);
    const currentMap = new Map(current.map(eq => [eq.equipment_id, eq]));
    const newMap = new Map(newEquipmentList.map(eq => [eq.equipment_id, eq]));
    // Remove deleted
    for (const eq of current) {
      if (!newMap.has(eq.equipment_id)) {
        await deleteEventEquipment(eq.id);
      }
    }
    // Update changed
    for (const eq of newEquipmentList) {
      if (currentMap.has(eq.equipment_id)) {
        const old = currentMap.get(eq.equipment_id);
        if (old.quantity !== eq.quantity) {
          await updateEventEquipment(old.id, eq.quantity);
        }
      }
    }
    // Add new
    for (const eq of newEquipmentList) {
      if (!currentMap.has(eq.equipment_id)) {
        await addEquipmentToEvent(eventId, [{ value: eq.equipment_id, quantity: eq.quantity }]);
      }
    }

    // Refresh
    const { data: updatedEquipment } = await getEventEquipment(eventId);
    setEquipment(updatedEquipment || []);
    updateGoogleEventEquipment(event.google_event_id, updatedEquipment);
  };

  const handleVenueTypeChange = async (newVenueType) => {
    try {
      setIsUpdatingVenueType(true);
      const { error } = await updateEventVenueType(eventId, newVenueType);
      
      if (error) throw error;
      
      // Update the local event state
      setEvent({ ...event, venue_type: newVenueType });
      setIsVenueTypeModalOpen(false);
    } catch (err) {
      console.error('Failed to update venue type:', err);
      alert('Failed to update venue type. Please try again.');
    } finally {
      setIsUpdatingVenueType(false);
    }
  };

  if (loading) {
    return <PageContainer><LoadingSpinner text="Loading Event..." /></PageContainer>;
  }
  
  if (error) {
    return <PageContainer>Error: {error}</PageContainer>;
  }
  
  if (!event) {
    return <PageContainer>Event not found.</PageContainer>;
  }

  return (
    <PageContainer>
      <Breadcrumb>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          <BreadcrumbLink to="/events">Events</BreadcrumbLink>
          <BreadcrumbSeparator>&gt;</BreadcrumbSeparator>
          <span>{event.title}</span>
        </div>
        <DeleteEventButton 
          eventId={eventId} 
          eventTitle={event.title} 
          {...managerPermissionProps}
        />
      </Breadcrumb>    
      <PageHeaderFlex>
        <EventInfoSection>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <PageTitle>{event.title}</PageTitle>
            </div>
            {event.company_name && (
              <EventCompany>
                {event.company_name}
                {event.account_rep?.name && (
                  <span style={{ 
                    color: '#999', 
                    fontSize: '0.9rem', 
                    fontWeight: 400 
                  }}>
                    • Sales Rep: <span style={{fontWeight: '700'}}>{event.account_rep.name}</span>
                  </span>
                )}
              </EventCompany>
            )}
            
            {event.expected_revenue && isManager && (
              <RevenueBadge revenue={event.expected_revenue} size="medium" />
            )}
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '.5rem 0' }}>
            <EventTypeBadge eventType={event.event_type} />
             <div 
                onClick={() => setIsStatusModalOpen(true)} 
                style={{ cursor: 'pointer' }}
              >
                <EventStatusBadge status={event.event_status} />
            </div>
            <VenueTypeIndicator 
              venueType={event.venue_type} 
              size="medium"
              onClick={() => setIsVenueTypeModalOpen(true)}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <EditEventDateTimeButton 
            event={event}
            onSuccess={handleDateTimeUpdate}
            assignments={assignments}
            user={user}
            {...managerPermissionProps}
          />
          

          <EventLocationLink
            event={event}
            isEditingLocation={isEditingLocation}
            editedLocationValue={editedLocationValue}
            handleLocationSelect={handleLocationSelect}
            handleCancelLocationEdit={handleCancelLocationEdit}
            savingLocation={savingLocation}
            editedLocation={editedLocation}
            handleEditLocation={handleEditLocation}
            isManager={isManager}
            isAuthenticated={isAuthenticated}
            onSuccess={handleLocationUpdate}
          />
          <TabsContainer>
            {tabList.map((tab, idx) => (
              <TabButton
                key={tab}
                $active={activeTab === tab}
                $position={idx === 0 ? "first" : idx === tabList.length - 1 ? "last" : undefined}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </TabButton>
            ))}
          </TabsContainer>
        </EventInfoSection>
      </PageHeaderFlex>

      <div>
        {activeTab === "Pre-Event" && (
          <div>

      <TwoColumnLayout>
      <div>

        <Card>
            <TeamMemberHeader>
              <h3 style={{ margin: 0 }}>Assigned Team Members</h3>
              <CountBadge>{assignments.length}</CountBadge>
            </TeamMemberHeader>
            <PersonList>
              {assignments.length === 0 ? (
                <p>No team members assigned yet.</p>
              ) : (
                assignments.map(assignment => (
                  <Person key={assignment.id}>
                    <PersonAvatar>
                      {getInitials(assignment.user?.name)}
                    </PersonAvatar>
                    <PersonInfo>
                      <PersonName>{assignment.user?.name}</PersonName>
                      <PersonRole>{assignment.user?.email || 'Team Member'}</PersonRole>
                    </PersonInfo>
                    <DeleteTeamMemberButton 
                      onRemove={handleRemoveEmployee}
                      assignmentId={assignment.id}
                      isManager={isManager}
                      isAuthenticated={isAuthenticated}
                    />
                  </Person>
                ))
              )}
            </PersonList>
            <div style={{ borderTop: '1px solid #eee'}}>
              <AddTeamMemberButton 
                onAdd={() => setIsAddingTeamMember(!isAddingTeamMember)} 
                isAdding={isAddingTeamMember}
                {...managerPermissionProps}
              />
              {isAddingTeamMember && (
                <EventAssignUserForm
                  eventId={eventId}
                  onSuccess={handleTeamMembersAdded}
                  onCancel={() => setIsAddingTeamMember(false)}
                  isVisible={true}
                  isEventPage={true}
                  assignedUsers={assignments}
                />
              )}
            </div>
          </Card>
          <Card>
            <h3>Event Equipment</h3>
            <EventEquipmentList>
              {equipment.length > 0 ? (
                equipment.map(equipment => (
                  <EventEquipmentItem key={equipment.id}>
                    {equipment.equipment.name}
                    <QuantityBadge>{equipment.quantity}</QuantityBadge>
                  </EventEquipmentItem>
                ))
              ) : (
                <p style={{ color: '#888', fontStyle: 'italic' }}>No equipment assigned for this event.</p>
              )}
            </EventEquipmentList>
            <ManageEventEquipmentButton
              eventId={eventId}
              currentEquipment={equipment}
              onEquipmentChange={handleEquipmentChange}
              isManager={isManager}
              isAuthenticated={isAuthenticated}
            />
          </Card>
        </div>

          <div>
        <Card>
            <h3>Event Contacts</h3>
            {memoizedContactsList}
            <AddEventContact
              isAddingContact={isAddingContact}
              newContact={newContact}
              setNewContact={setNewContact}
              onCancel={setIsAddingContact}
              onSave={handleAddContact}
              isManager={isManager}
              isAuthenticated={isAuthenticated}
            />
          </Card>
        </div>
        </TwoColumnLayout>
                <Card>
            <DescriptionHeading>
              <h3>Event Notes</h3>
              {!isEditingDescription && (
                <EditButton onClick={handleEditDescription} title="Edit notes">
                  <SquarePen size={16} />
                </EditButton>
              )}
            </DescriptionHeading>
            
            <DescriptionContainer>
              {isEditingDescription ? (
                <>
                  <DescriptionTextarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Enter event notes..."
                  />
                  <SaveCancelButtons>
                    <DefaultButton onClick={handleCancelEdit}>
                      Cancel
                    </DefaultButton>
                    <PrimaryButton 
                      onClick={handleSaveDescription}
                      disabled={savingDescription}
                    >
                      {savingDescription ? 'Saving...' : 'Save'}
                    </PrimaryButton>
                  </SaveCancelButtons>
                </>
              ) : (
                <p>{event.description || 'No description provided.'}</p>
              )}
            </DescriptionContainer>
          </Card>
          </div>
        )}
        {activeTab === "During Event" && (
          <div>
            <Card>
            <DescriptionHeading>
              <h3>Event Notes</h3>
              {!isEditingDescription && (
                <EditButton onClick={handleEditDescription} title="Edit notes">
                  <SquarePen size={16} />
                </EditButton>
              )}
            </DescriptionHeading>
            
            <DescriptionContainer>
              {isEditingDescription ? (
                <>
                  <DescriptionTextarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Enter event notes..."
                  />
                  <SaveCancelButtons>
                    <DefaultButton onClick={handleCancelEdit}>
                      Cancel
                    </DefaultButton>
                    <PrimaryButton 
                      onClick={handleSaveDescription}
                      disabled={savingDescription}
                    >
                      {savingDescription ? 'Saving...' : 'Save'}
                    </PrimaryButton>
                  </SaveCancelButtons>
                </>
              ) : (
                <p>{event.description || 'No description provided.'}</p>
              )}
            </DescriptionContainer>
          </Card>

          <Card>
            <h3>Report Issues</h3>
            <IssueForm onSubmit={handleIssueSubmit}>
              <IssueOptions>
                <IssueLabel>
                  <input type="checkbox" name="issue" value="Equipment" /> Equipment
                </IssueLabel>
                <IssueLabel>
                  <input type="checkbox" name="issue" value="Location" /> Location
                </IssueLabel>
                <IssueLabel>
                  <input type="checkbox" name="issue" value="Weather" /> Weather
                </IssueLabel>
                <IssueLabel>
                  <input type="checkbox" name="issue" value="Technology" /> Technology
                </IssueLabel>
                <IssueLabel>
                  <input type="checkbox" name="issue" value="Team Member" /> Team Member
                </IssueLabel>
              </IssueOptions>
              <IssueTextarea
                name="description"
                placeholder="Describe the issue (optional)"
              />
              <IssueSubmitButton type="submit" disabled={submittingIssue}>
                {submittingIssue ? 'Submitting...' : 'Submit'}
              </IssueSubmitButton>
            </IssueForm>

            {issues.length > 0 && (
              <>
                <IssuesDivider />
                <IssuesHeader>
                  <IssuesTitle>Reported Issues</IssuesTitle>
                  <IssuesCount>{issues.length} {issues.length === 1 ? 'Issue' : 'Issues'}</IssuesCount>
                </IssuesHeader>
                <IssuesList>
                  {issues.map(issue => (
                    <IssueItem key={issue.id}>
                      {issue.issue_type.split(', ').map((type, index) => (
                        <IssueType key={index}>{type}</IssueType>
                      ))}
                      {issue.description && (
                        <IssueDescription>{issue.description}</IssueDescription>
                      )}
                      <IssueMeta>
                        <Timestamp>• {formatDate(issue.created_at, 'issue')}</Timestamp>
                      </IssueMeta>
                    </IssueItem>
                  ))}
                </IssuesList>
              </>
            )}
          </Card>

          <Card>
            <IssuesHeader>
            <h3 style={{marginBottom: '0'}}>Event Photos</h3>
            <IssuesCount>{uploads.length} {uploads.length === 1 ? 'Attachment' : 'Attachments'}</IssuesCount>
            </IssuesHeader>
            <MediaUploader onUpload={handleUpload} eventId={eventId} />
          </Card>
          </div>
        )}
        {activeTab === "Post-Event" && (
          <div>
           <Card>
            <h3>Event Rating</h3>
            <IssueForm onSubmit={handleRatingSubmit}>
              <IssueOptions>
                <IssueLabel>
                  <input 
                    type="radio" 
                    name="rating" 
                    value="Awesome" 
                    checked={selectedRating === 'Awesome'}
                    onChange={(e) => setSelectedRating(e.target.value)}
                    disabled={isRatingSubmitted}
                  /> Awesome
                </IssueLabel>
                <IssueLabel>
                  <input 
                    type="radio" 
                    name="rating" 
                    value="Satisfactory" 
                    checked={selectedRating === 'Satisfactory'}
                    onChange={(e) => setSelectedRating(e.target.value)}
                    disabled={isRatingSubmitted}
                  /> Satisfactory
                </IssueLabel>
                <IssueLabel>
                  <input 
                    type="radio" 
                    name="rating" 
                    value="Needs Improvement" 
                    checked={selectedRating === 'Needs Improvement'}
                    onChange={(e) => setSelectedRating(e.target.value)}
                    disabled={isRatingSubmitted}
                  /> Needs Improvement
                </IssueLabel>
              </IssueOptions>
              {ratingError && (
                <div style={{ color: 'red', marginTop: '0.5rem' }}>
                  {ratingError}
                </div>
              )}
              {!isRatingSubmitted && (
                <IssueSubmitButton type="submit">Submit Rating</IssueSubmitButton>
              )}
            </IssueForm>

            <IssueLabel style={{ marginTop: '1rem' }}>
              <input type="checkbox" name="issue" value="post_event_email_sent" disabled/> Post-Event Email Sent
            </IssueLabel>
            
            <IssueLabel style={{ marginTop: '1rem' }}>
              <input type="checkbox" name="issue" value="send_survey_to_event_contacts" disabled/> Send Survey to event contacts
            </IssueLabel>
          </Card>
          </div>
        )}
      </div>
      {isStatusModalOpen && (
        <SelectionModal
          isOpen={isStatusModalOpen}
          onClose={() => setIsStatusModalOpen(false)}
          title="Change Event Status"
          currentValue={event.event_status}
          onSelect={handleStatusChange}
          options={[
            { value: 'Hold', label: 'Hold' },
            { value: 'In_Progress', label: 'In Progress' },
            { value: 'Complete', label: 'Complete' },
            { value: 'Canceled', label: 'Canceled' }
          ]}
          renderOption={(option) => <EventStatusBadge status={option.value} />}
        />
      )}

      {isVenueTypeModalOpen && (
        <SelectionModal
          isOpen={isVenueTypeModalOpen}
          onClose={() => setIsVenueTypeModalOpen(false)}
          title="Change Venue Type"
          currentValue={event.venue_type}
          onSelect={handleVenueTypeChange}
          options={[
            { value: 'Indoor', label: 'Indoor' },
            { value: 'Outdoor', label: 'Outdoor' }
          ]}
          renderOption={(option) => (
            <VenueTypeIndicator 
              venueType={option.value} 
              size="medium"
            />
          )}
        />
      )}
    </PageContainer>
  );
};

export default EventDetailsPage;